"use client";
import React, { useEffect, useState, useRef } from "react";
import Head from "next/head";
import {
  Container,
  Divider,
  Grid,
  GridRow,
  GridColumn,
} from "semantic-ui-react";

import Layout from "../../../../container";
import NhrankingList from "../../../../components/Ranking/NhrankingList";
import RankingMap from "../../../../components/Ranking/rankingmap";
import MapTabs from "../../../../components/MapTabs";
import ViewToggle from "../../../../components/ViewToggle";
import NhRankingSearchSuggestion from "../../../../components/NhRankingSearchSuggestion";
import ShareModal from "../../../../pages/Neighborhood/ShareModal";

import SearchBar from "../../component/SearchBar";
import CityDescription from "../../component/CityDescription";
import ShareButton from "../../component/ShareButton";

import { fetchNeighborhoods } from "../../component/rankingApi";
import { separateCityAndState } from "../../../../common/Utils";
import { geocodeByAddress, getLatLng } from "react-places-autocomplete";

let recordCount = 0;

export default function Nhranking() {
  const scrollerRef = useRef(null);

  const [state, setState] = useState({
    items: [],
    nhmarkerdata: [],
    loading: false,
    emptyRecords: false,
    activePage: 1,
    isMobile: false,
    selectedFilter: "Overall Score",
    filterval: "npi",
    cityname: "",
    statename: "",
    datamsg: "Loading...",
    gmapsLoaded: false,
    userInput: "",
    results: [],
    showSuggestions: false,
    activeSuggestion: 0,
    lat: "",
    lng: "",
    isShareModalOpen: false,
  });

  const [mounted, setMounted] = useState(false);
  const hasScrolledRef = useRef(false);
  const scrollingRef = useRef(false);

  /** ---------- City coordinates ---------- */
  const setCityCoordinates = async (city, stateName) => {
    const cityCoordinates = {
      "San Francisco": { lat: 37.75771992816863, lng: -122.43759999999999 },
      Peninsula: { lat: 37.586439, lng: -122.3578237 },
      "Silicon Valley": { lat: 37.2963154, lng: -121.9805778 },
      "East Bay": { lat: 37.7837329, lng: -122.1198028 },
      Marin: { lat: 37.9567704, lng: -122.5497414 },
    };

    if (cityCoordinates[city]) {
      setState((prev) => ({
        ...prev,
        lat: cityCoordinates[city].lat,
        lng: cityCoordinates[city].lng,
      }));
      return;
    }

    try {
      const results = await geocodeByAddress(`${city}, ${stateName}`);
      const { lat, lng } = await getLatLng(results[0]);
      setState((prev) => ({ ...prev, lat, lng }));
    } catch (err) {
      console.error("Error geocoding:", err);
      setState((prev) => ({ ...prev, lat: 36.7783, lng: -119.4179 })); // fallback
    }
  };

  /** ---------- Stable marker updater (prevents blinking) ---------- */
  const lastVisibleRef = useRef([]);
  let rafId = null;

  const updateVisibleMarkers = (newVisibleItems) => {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      const lastIds = lastVisibleRef.current.map((i) => i.id);
      const newIds = newVisibleItems.map((i) => i.id);

      if (JSON.stringify(lastIds) !== JSON.stringify(newIds)) {
        lastVisibleRef.current = newVisibleItems;
        setState((prev) => ({ ...prev, nhmarkerdata: newVisibleItems }));
      }
    });
  };

  /** ---------- IntersectionObserver logic ---------- */
  const observeVisibleItems = (items) => {
    const minVisibleItems = 3;
    const visibleItemIds = new Set();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const element = entry.target;
          const anchor = element.querySelector("a.nhrankking_img");
          if (!anchor) return;

          const itemId = anchor.getAttribute("data-testid");
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            visibleItemIds.add(itemId);
          } else {
            visibleItemIds.delete(itemId);
          }
        });

        if (visibleItemIds.size >= minVisibleItems) {
          const visibleItems = items.filter((it) =>
            visibleItemIds.has(String(it.id)),
          );
          const newVisibleItems = visibleItems.slice(0, 3);
          updateVisibleMarkers(newVisibleItems);
        }
      },
      { threshold: 0.6 }, // stricter threshold
    );

    const elements = document.querySelectorAll(".nhranking_item");
    elements.forEach((el) => observer.observe(el));

    return observer;
  };

  /** ---------- Fetch Data ---------- */
  const getNeighborhoodData = async (count, filter) => {
    setState((prev) => ({ ...prev, loading: true }));
    const slug = window.location.href;
    const parts = slug.split("/");
    const pageurl = parts[parts.length - 2];
    const { city_name, state_name } = separateCityAndState(pageurl);

    setState((prev) => ({
      ...prev,
      cityname: city_name,
      statename: state_name,
      userInput: `${city_name}, ${state_name}`,
    }));

    const data = await fetchNeighborhoods({
      start: count,
      limit: 10,
      filter,
      city: city_name,
      state: state_name,
    });

    if (data.length > 0) {
      setState((prev) => {
        const newItems = prev.items.concat(data);
        return {
          ...prev,
          datamsg: "",
          nhmarkerdata:
            prev.items.length === 0 ? data.slice(0, 10) : prev.nhmarkerdata,
          items: newItems,
          loading: false,
        };
      });
      if (recordCount === 0) setCityCoordinates(city_name, state_name);
    } else if (count === 0) {
      setState((prev) => ({
        ...prev,
        datamsg: "No data found",
        loading: false,
      }));
    } else {
      setState((prev) => ({ ...prev, emptyRecords: true, loading: false }));
    }
  };

  /** ---------- Infinite Scroll ---------- */
  const handleWindowScroll = () => {
    if (scrollingRef.current) return;
    scrollingRef.current = true;

    window.requestAnimationFrame(() => {
      scrollingRef.current = false;

      if (!hasScrolledRef.current) {
        hasScrolledRef.current = true;
      }

      const { scrollTop, scrollHeight } = document.documentElement;
      const winH = window.innerHeight;
      const nearBottom = scrollTop + winH >= scrollHeight - 100;

      if (nearBottom && !state.loading && !state.emptyRecords) {
        recordCount += 10;
        getNeighborhoodData(recordCount, state.filterval);
      }
      // ✅ Observer will handle markers, no manual update needed
    });
  };

  /** ---------- Effects ---------- */
  useEffect(() => {
    setMounted(true);
    recordCount = 0;
    hasScrolledRef.current = false;
    getNeighborhoodData(recordCount, state.filterval);
    setState((prev) => ({ ...prev, isMobile: window.innerWidth <= 767 }));

    const handleResize = () =>
      setState((prev) => ({ ...prev, isMobile: window.innerWidth <= 767 }));
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleWindowScroll, { passive: true });

    setTimeout(
      () => setState((prev) => ({ ...prev, gmapsLoaded: true })),
      1500,
    );

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleWindowScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasScrolledRef.current && !state.isMobile && state.items.length > 0) {
      const observer = observeVisibleItems(state.items);
      return () => observer.disconnect();
    }
  }, [state.items, state.isMobile]);

  /** ---------- Tab Change ---------- */
  const handleTabChange = (value, displayText, urlText) => {
    const slug = window.location.href;
    const parts = slug.split("/");
    const pageurl = parts[parts.length - 2];
    recordCount = 0;
    hasScrolledRef.current = false;
    window.location.href = `/Nhranking/${urlText}/${pageurl}`;
  };

  /** ---------- Share + Suggestions ---------- */
  const handleShareButtonClick = () =>
    setState((p) => ({ ...p, isShareModalOpen: true }));
  const handleCloseShareModal = () =>
    setState((p) => ({ ...p, isShareModalOpen: false }));

  const handleNhRankingSearchSuggestion = (location) => {
    window.location.href = `/Nhranking/best-overall/${location.cityUrl}`;
  };

  const handleViewClick = () => {
    localStorage.setItem("ispagemain", true);
    localStorage.setItem("selectdnhranking", state.filterval);
    window.open(
      `/?address=${state.cityname}, ${state.statename}, USA`,
      "_blank",
    );
  };

  if (!mounted) return null;

  return (
    <Layout>
      <Container fluid style={{ padding: "0em 5em 5em" }}>
        <Head>
          <title>
            Neighborhood Ranking
            {state.cityname && `: ${state.cityname}, ${state.statename}`}
          </title>
          <meta
            name="description"
            content={`See how neighborhoods in ${state.cityname} rank based on schools, safety, amenities, and best value.`}
          />
          <meta
            property="og:image"
            content={
              process.env.NEXT_PUBLIC_CDN_ID + "static/images/seoimg.png"
            }
          />
        </Head>

        <div className="homeaccount_main common_contain_main">
          <div className="nhranking_main">
            <h2 className="NhrankingTitles">
              2025 Top Neighborhoods by Overall Score in:
            </h2>

            <div className="search_welcome_main">
              <SearchBar
                value={state.userInput}
                onChange={(val) => setState((p) => ({ ...p, userInput: val }))}
                onKeyDown={() => {}}
                onSelect={(val) => setState((p) => ({ ...p, userInput: val }))}
                showSuggestions={state.showSuggestions}
                results={state.results}
                rankingType="overall"
              />
            </div>

            <NhRankingSearchSuggestion
              onSelect={handleNhRankingSearchSuggestion}
              ispage="nhranking"
            />
          </div>

          <div className="nhranking-mobile-share">
            <div className="share-button-container nhranking-share-button">
              <ShareButton onClick={handleShareButtonClick} />
            </div>
          </div>

          <Divider />

          <Grid divided stackable>
            <GridRow className="content_grid">
              <GridColumn width={10}>
                <MapTabs onChange={handleTabChange} defaultactiveTab="npi" />
                <ViewToggle
                  onToggle={handleViewClick}
                  defaultSelected="Top Lists"
                />

                <div className="nhranking_top_description">
                  <CityDescription
                    filter={state.selectedFilter}
                    city={state.cityname}
                  />
                </div>

                <NhrankingList
                  itemData={state.items}
                  isMobile={state.isMobile}
                  active={state.activePage}
                  datamsg={state.datamsg}
                  ref={scrollerRef}
                  loading={state.loading}
                />
              </GridColumn>

              <GridColumn width={6} className="nh_map_section">
                <div className="share-button-container nhranking-share-button">
                  <ShareButton onClick={handleShareButtonClick} />
                  <ShareModal
                    isShareModalOpen={state.isShareModalOpen}
                    handleCloseShareModal={handleCloseShareModal}
                  />
                </div>

                {state.lat && state.lng ? (
                  <div className="sticky-container">
                    <div className="nhrankingmap_img">
                      <h2 className="NhrankingTitles">
                        Top Neighborhoods by Overall Score
                      </h2>
                      <RankingMap
                        lat={state.lat}
                        lng={state.lng}
                        cityname={state.cityname}
                        selectedfilter={state.selectedFilter}
                        nhmarkerdata={state.nhmarkerdata}
                        itemData={state.items}
                      />
                      <p onClick={handleViewClick} className="fullmapview">
                        View Full Map
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="map_intial_loader">
                    <p>Loading..</p>
                  </div>
                )}
              </GridColumn>
            </GridRow>
          </Grid>
        </div>
      </Container>
    </Layout>
  );
}
